import { useEffect, useState } from "react";

const SitePreview = ({
  url,
  alt = "",
  className = "",
  imgClassName = "",
  fallbackLogo = "/logo.png",
}) => {
  const [src, setSrc] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | loaded | error

  // Generate screenshot URL
  useEffect(() => {
    if (!url) {
      setStatus("error");
      return;
    }

    // Use thum.io — reliable free screenshot API
    const screenshotUrl = `https://image.thum.io/get/width/800/crop/500/noanimate/${url}`;
    setSrc(screenshotUrl);
  }, [url]);

  const handleLoad = () => {
    setStatus("loaded");
  };

  const handleError = () => {
    // If primary screenshot fails, try secondary service
    if (src && src.includes("thum.io")) {
      // Try microlink as fallback
      const fallbackUrl = `https://api.microlink.io/?url=${encodeURIComponent(url)}&screenshot=true&meta=false&embed=screenshot.url`;
      setSrc(fallbackUrl);
    } else {
      // Both failed — show logo
      setStatus("error");
    }
  };

  return (
    <div className={`relative overflow-hidden bg-slate-100 ${className}`}>
      {/* Loading skeleton */}
      {status === "loading" && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-slate-100">
          <div className="relative">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin" />
          </div>
          <p className="text-[10px] font-bold text-slate-400 mt-1">
            Loading preview...
          </p>
        </div>
      )}

      {/* Error / Fallback — show your logo */}
      {status === "error" && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-50 to-slate-100">
          <img
            src={fallbackLogo}
            alt={alt || "Site preview"}
            className="h-10 w-auto object-contain opacity-40"
            onError={(e) => {
              // If logo also fails, show text fallback
              e.target.style.display = "none";
            }}
          />
          <div className="text-center px-4">
            <p className="text-xs font-bold text-slate-500">
              {alt || "Preview"}
            </p>
            {url && (
              <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[200px]">
                {url.replace("https://", "").replace("www.", "")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Actual screenshot image */}
      {src && status !== "error" && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover object-top transition-opacity duration-500 ${
            status === "loaded" ? "opacity-100" : "opacity-0"
          } ${imgClassName}`}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      )}
    </div>
  );
};

export default SitePreview;
